import ArrowLeft16 from '@carbon/icons-react/es/arrow--left/16';
import ArrowRight16 from '@carbon/icons-react/es/arrow--right/16';
import { Carousel } from 'antd';
import React, { FC, Fragment, useRef, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Goal } from 'api/goal';
import { Button } from 'components/atoms/button';
import { RichTextEditor } from 'components/atoms/rich-text-editor';
import { HelperText01, Text03 } from 'components/styles';
import { GoalModal } from '../goal-modal';
import { CarouselActionBar } from './_action-bar';

const Wrapper = styled.div`
  --rte-readonly-content-padding: 0 20% 0 0;
  height: 100%;
  overflow: hidden;

  .ant-carousel {
    height: calc(100% - 40px);
    overflow: hidden;
  }
`;

const Count = styled.span`
  ${HelperText01};
  ${Text03};
  margin-right: var(--spacing-03);
`;

const Footer = styled.div`
  align-items: center;
  display: flex;
  justify-content: flex-end;
`;

const StyledCarousel = styled(Carousel)`
  &,
  .slick-slider,
  .slick-list,
  .slick-track,
  .slick-slide,
  .slick-slide > div {
    height: 100%;
  }

  .slick-slide > div {
    position: relative;
  }
`;

const Content = styled(RichTextEditor)`
  position: absolute;
  top: 70px;
  bottom: 0;
  width: 100%;

  .ck-content {
    height: 100%;
  }

  .ck.ck-editor__editable_inline > :last-child {
    margin-bottom: var(--layout-01);
  }
`;

export const DataContent: FC<{
  data: Goal[];
  projectId: number;
}> = ({ data, projectId }) => {
  const sliderRef = useRef<any>(null);
  const [showGoalModal, setShowGoalModal] = useState(false);
  const [currentGoalToEdit, setCurrentGoalToEdit] =
    useState<Goal | undefined>();
  const [currentSlide, setCurrentSlide] = useState<number>(1);

  const onPrev = () => {
    sliderRef.current?.prev();
    setCurrentSlide(currentSlide - 1);
  };
  const onNext = () => {
    sliderRef.current?.next();
    setCurrentSlide(currentSlide + 1);
  };
  const onEdit = (goal: Goal) => {
    setCurrentGoalToEdit(goal);
    setShowGoalModal(true);
  };
  const onClose = () => {
    setShowGoalModal(false);
    setCurrentGoalToEdit(undefined);
  };

  return (
    <Wrapper>
      <StyledCarousel dots={false} infinite={false} ref={sliderRef}>
        {data.map((goal) => {
          return (
            <Fragment key={goal.goalId}>
              <CarouselActionBar
                goal={goal}
                projectId={projectId}
                onEdit={onEdit}
              />
              <h6>{goal.goalName}</h6>
              <Content data={goal.goalDescription} readonly />
            </Fragment>
          );
        })}
      </StyledCarousel>
      <Footer>
        <Count>
          <FormattedMessage
            id="pagination.carousel"
            values={{ page: currentSlide, total: data.length }}
          />
        </Count>
        <Button
          disabled={currentSlide === 1}
          icon={<ArrowLeft16 />}
          size="small"
          type="text"
          onClick={onPrev}
        />
        <Button
          disabled={currentSlide === data.length}
          icon={<ArrowRight16 />}
          size="small"
          type="text"
          onClick={onNext}
        />
      </Footer>
      {currentGoalToEdit && (
        <GoalModal
          data={currentGoalToEdit}
          projectId={projectId}
          visible={showGoalModal}
          onClose={onClose}
        />
      )}
    </Wrapper>
  );
};
